import { DocumentTemplateType, FileCategoryType, FileNode } from '../../api-client/autogenerated';
import { MULTI_PART_FILE_SIZE } from '../../scripts/constants';
import { insertDocument, modifyDocumentById } from './documents';
import {
  importBidTabulationsFileFromDesign,
  importGeneralDocumentFileFromDesign,
  uploadBidTabulationsFile,
  uploadGeneralDocumentFile,
} from './filesystem';
import { getFileById } from './files';
import { patchProject } from './project';
import { getTemplateId } from './templates';

/**
 * Publishing a bidding document, independent of the Bid Management page.
 *
 * These were inline in that page's save, which meant the only way to publish an addendum was to go
 * there and press Save. Nothing in them depends on the bid setup being saved in the same pass --
 * each is an insertDocument followed by its file uploads -- so they live here and both the page and
 * the Create Anything card call them.
 */

export type BiddingUploadFile = File | FileNode;

/** Progress of the file currently uploading, 0-100. */
export type UploadProgressHandler = (event: { loaded: number; total: number }) => void;

/** Called before each file starts, so a caller can say which one it is waiting on. */
export type UploadFileStartHandler = (fileName: string, index: number, total: number) => void;

export type UploadHandlers = {
  onProgress?: UploadProgressHandler;
  onFileStart?: UploadFileStartHandler;
  /** Every file has been sent and S3 verification is being waited on -- no progress to report. */
  onVerifying?: () => void;
};

const getFileName = (file: BiddingUploadFile) =>
  (file as File).name || (file as FileNode).relativeKey || 'file';

const isFromFileManager = (file: BiddingUploadFile) => (file as FileNode).relativeKey !== undefined;

/**
 * Waits for S3 to report every uploaded file as verified.
 *
 * A document whose files are not yet verified is not usable yet, so callers wait before telling the
 * user the upload is done. Mutates nothing the caller owns -- the ids are copied first.
 */
export const waitForFilesToBeVerified = async (fileIds: string[]): Promise<void> => {
  const pending = [...fileIds];
  return new Promise<void>((resolve) => {
    if (pending.length === 0) return resolve();
    const interval = setInterval(async () => {
      for (let i = 0; i < pending.length; i += 1) {
        const fileId = pending[i];
        const { isVerified } = await getFileById(fileId);
        if (isVerified) {
          const verifiedIndex = pending.findIndex((id) => id === fileId);
          if (verifiedIndex !== -1) pending.splice(verifiedIndex, 1);
        }
      }
      if (pending.length === 0) {
        clearInterval(interval);
        resolve();
      }
    }, 3000);
  });
};

/** Attaches files to a document that already exists, returning the ids of the files created. */
const uploadDocumentFiles = async (
  documentId: string,
  files: BiddingUploadFile[],
  fileType: FileCategoryType,
  handlers: UploadHandlers = {},
): Promise<string[]> => {
  const fileIds: string[] = [];
  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    handlers.onFileStart?.(getFileName(file), index + 1, files.length);
    if (isFromFileManager(file)) {
      const fileNode = file as FileNode;
      const response = await importGeneralDocumentFileFromDesign(documentId, fileType, {
        ownerId: fileNode.ownerId,
        fullKey: fileNode.fullKey,
        fullFileName: fileNode.relativeKey!,
        projectId: fileNode.projectId,
      });
      if (response.file) fileIds.push(response.file.id);
    } else {
      const response = await uploadGeneralDocumentFile(
        documentId,
        {
          fullFileName: (file as File).name,
          fileType,
          useMultiPartUpload: (file as File).size > MULTI_PART_FILE_SIZE,
        },
        file as File,
        handlers.onProgress,
      );
      if (response.file) fileIds.push(response.file.id);
    }
  }
  return fileIds;
};

/**
 * An addendum, locked once its files land. Locking is what makes it an issued addendum rather than
 * a draft, so it happens only after every file is verified.
 */
export const uploadAddendum = async (
  params: {
    projectId: string;
    creatorUserId: string;
    title: string;
    files: BiddingUploadFile[];
  },
  handlers: UploadHandlers = {},
) => {
  const addendum = await insertDocument({
    documentTemplateId: await getTemplateId(DocumentTemplateType.Addenda),
    projectId: params.projectId,
    creatorUserId: params.creatorUserId,
    title: params.title,
  });
  const fileIds = await uploadDocumentFiles(
    addendum.id,
    params.files,
    FileCategoryType.BidAddendas,
    handlers,
  );
  handlers.onVerifying?.();
  await waitForFilesToBeVerified(fileIds);
  await modifyDocumentById(addendum.id, { patch: { isFileLocked: true } });
  return addendum;
};

export const uploadInformationalItem = async (
  params: {
    projectId: string;
    creatorUserId: string;
    title: string;
    files: BiddingUploadFile[];
  },
  handlers: UploadHandlers = {},
) => {
  const informationalItem = await insertDocument({
    documentTemplateId: await getTemplateId(DocumentTemplateType.InformationalItems),
    projectId: params.projectId,
    creatorUserId: params.creatorUserId,
    title: params.title,
  });
  const fileIds = await uploadDocumentFiles(
    informationalItem.id,
    params.files,
    FileCategoryType.DocumentBase,
    handlers,
  );
  handlers.onVerifying?.();
  await waitForFilesToBeVerified(fileIds);
  return informationalItem;
};

/** The advertisement carries no title of its own -- it is one file on one document. */
export const uploadBidAdvertisement = async (
  params: { projectId: string; creatorUserId: string; file: BiddingUploadFile },
  handlers: UploadHandlers = {},
) => {
  const advertisement = await insertDocument({
    documentTemplateId: await getTemplateId(DocumentTemplateType.BidAdvertisement),
    projectId: params.projectId,
    creatorUserId: params.creatorUserId,
  });
  await uploadDocumentFiles(
    advertisement.id,
    [params.file],
    FileCategoryType.DocumentBase,
    handlers,
  );
  return advertisement;
};

/**
 * The completed tabulation is a file on the project rather than a document, so it is uploaded to the
 * project and then pointed at by bidTabulationsFileId.
 */
export const uploadCompletedBidTabulation = async (
  params: { projectId: string; file: BiddingUploadFile; isPublic?: boolean },
  handlers: UploadHandlers = {},
) => {
  const { projectId, file, isPublic } = params;
  handlers.onFileStart?.(getFileName(file), 1, 1);
  const response = isFromFileManager(file)
    ? await importBidTabulationsFileFromDesign(projectId, {
        ownerId: (file as FileNode).ownerId,
        fullKey: (file as FileNode).fullKey,
        fullFileName: (file as FileNode).relativeKey!,
        projectId: (file as FileNode).projectId,
        isPublic,
      })
    : await uploadBidTabulationsFile(
        projectId,
        {
          fullFileName: (file as File).name,
          isPublic,
          useMultiPartUpload: (file as File).size > MULTI_PART_FILE_SIZE,
        },
        file as File,
        handlers.onProgress,
      );
  if (response?.file) {
    await patchProject(projectId, { bidTabulationsFileId: response.file.id });
  }
  return response;
};
